import { c as _c } from "react/compiler-runtime";
import { StepperFormStep } from "@/components/organisms/StepperForm/schema";
import { useTranslations } from "next-intl";
import { useNewRoomListingMutation } from "../api/mutations/useNewRoomListing";
import { useAdminCreateListing } from "@/components/modules/admin/api/mutations/useAdminCreateListing";
import { useAppForm } from "@/hooks/useAppForm";
import { getAddNewRoomListingSchema, AddNewRoomListingSchemaType, Gender } from "../api/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useRoomListing } from "../api/queries/useRoomListing";
import { mapListingToCreateForm } from "./duplicatePrefill";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ListingType } from "../api/schema";
import { RoomDetails } from "./components/RoomDetails/RoomDetails";
import { applyUnitAssociation } from "./components/house-details/property-association-select/applyUnitAssociation";
import type { BusinessUnitsSchemaType, GetRealEstateManagementSchemaType } from "@/components/modules/portfolio/api/schemas/schemas";
import { NewRoomListingImagesForm } from "./components/new-room-listing-images-form/NewRoomListingImagesForm";
import { useQueryClient } from "@tanstack/react-query";
import { USER_LISTINGS_QUERY_KEYS } from "../api/queries/useUserListings";
import useSession from "@/hooks/QueryHooks/useSession";
import { HouseDetails } from "./components/house-details/HouseDetails";
import { PropertyAssociationSelect } from "./components/house-details/property-association-select/PropertyAssociationSelect";
import { RevisionDetails } from "./components/revision-details/RevisionDetails";
import { ListingTypePrescreen } from "./components/listing-type-prescreen/ListingTypePrescreen";
type Props = {
  onSubmitSuccess?: (listingId: string, status?: string) => void;
  defaultListingType?: string;
  adminTargetUserId?: string;
  duplicateFromId?: string;
  prefillAssociation?: {
    unit: BusinessUnitsSchemaType;
    portfolio: GetRealEstateManagementSchemaType;
  };
};
export function useNewRoomListing(t0) {
  const $ = _c(110);
  const {
    onSubmitSuccess,
    defaultListingType,
    adminTargetUserId,
    duplicateFromId,
    prefillAssociation
  } = t0;
  const newRoomListingMutation = useNewRoomListingMutation();
  const adminCreateListingMutation = useAdminCreateListing();
  const isAdminFlow = !!adminTargetUserId;
  const tRoomListing = useTranslations("modules.listings.addRoomListing");
  const {
    data: session
  } = useSession();
  const isEmailVerified = !!session?.emailVerified;
  const tValidation = useTranslations("modules.listings.addRoomListing.validation");
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== tValidation) {
    t1 = getAddNewRoomListingSchema(tValidation);
    $[0] = tValidation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const schema = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Date();
    t5 = [];
    t6 = [];
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  const t7 = defaultListingType as ListingType ?? undefined;
  let t8;
  if ($[7] !== t7) {
    t8 = {
      title: "",
      rentPrice: undefined,
      deposit: undefined,
      description: undefined,
      latitude: undefined,
      longitude: undefined,
      locationNormalizedName: undefined,
      parishName: undefined,
      municipalityName: undefined,
      districtName: undefined,
      locationCodes: t2,
      images: t3,
      propertyType: "room",
      businessType: "roomRent",
      gender: Gender.ANY,
      maxPersons: undefined,
      availableFrom: t4,
      roomFeatures: t5,
      houseFeatures: t6,
      matchAlertsEnabled: true,
      listingType: t7,
      associatedUnitId: undefined,
      associatedUnitTitle: undefined,
      associatedPortfolioTitle: undefined,
      associatedPropertyImages: undefined
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== schema) {
    t9 = zodResolver(schema);
    $[9] = schema;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8 || $[12] !== t9) {
    t10 = {
      defaultValues: t8,
      resolver: t9
    };
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const form = useAppForm(t10);
  const t11 = duplicateFromId ?? "";
  let t12;
  if ($[14] !== t11) {
    t12 = {
      listingId: t11
    };
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const duplicateSource = useRoomListing(t12);
  const duplicateApplied = useRef(false);
  let t13;
  if ($[16] !== duplicateFromId || $[17] !== duplicateSource.data?.data || $[18] !== form) {
    t13 = () => {
      if (!duplicateFromId || duplicateApplied.current) {
        return;
      }
      const src = duplicateSource.data?.data;
      if (!src) {
        return;
      }
      duplicateApplied.current = true;
      form.reset(mapListingToCreateForm(src));
    };
    $[16] = duplicateFromId;
    $[17] = duplicateSource.data?.data;
    $[18] = form;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== duplicateFromId || $[21] !== duplicateSource.data || $[22] !== form) {
    t14 = [duplicateFromId, duplicateSource.data, form];
    $[20] = duplicateFromId;
    $[21] = duplicateSource.data;
    $[22] = form;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  useEffect(t13, t14);
  const prefillApplied = useRef(false);
  let t15;
  if ($[24] !== form.setValue || $[25] !== prefillAssociation) {
    t15 = () => {
      if (!prefillAssociation || prefillApplied.current) {
        return;
      }
      prefillApplied.current = true;
      applyUnitAssociation(form.setValue, prefillAssociation);
    };
    $[24] = form.setValue;
    $[25] = prefillAssociation;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== form || $[28] !== prefillAssociation) {
    t16 = [prefillAssociation, form];
    $[27] = form;
    $[28] = prefillAssociation;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  useEffect(t15, t16);
  let t17;
  if ($[30] !== adminCreateListingMutation || $[31] !== adminTargetUserId || $[32] !== form || $[33] !== isAdminFlow || $[34] !== newRoomListingMutation || $[35] !== onSubmitSuccess || $[36] !== queryClient) {
    t17 = (values, status) => {
      const isPublished = status ?? "active";
      const data = {
        ...values,
        isPublished
      } as AddNewRoomListingSchemaType;
      const onSuccess = response => {
        form.reset();
        queryClient.invalidateQueries({
          queryKey: USER_LISTINGS_QUERY_KEYS.all
        });
        if (response.data.id) {
          onSubmitSuccess?.(response.data.id, isPublished);
        }
      };
      if (isAdminFlow && adminTargetUserId) {
        adminCreateListingMutation.mutate({
          data,
          userId: adminTargetUserId
        }, {
          onSuccess
        });
        return;
      }
      newRoomListingMutation.mutate({
        data
      }, {
        onSuccess
      });
    };
    $[30] = adminCreateListingMutation;
    $[31] = adminTargetUserId;
    $[32] = form;
    $[33] = isAdminFlow;
    $[34] = newRoomListingMutation;
    $[35] = onSubmitSuccess;
    $[36] = queryClient;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleOnSubmit = t17;
  let t18;
  if ($[38] !== form.control) {
    t18 = {
      control: form.control,
      name: "listingType"
    };
    $[38] = form.control;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const listingType = useWatch(t18);
  const isDemand = listingType === ListingType.DEMAND;
  let t19;
  if ($[40] !== form.control) {
    t19 = {
      control: form.control,
      name: "associatedUnitTitle"
    };
    $[40] = form.control;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const associatedUnitTitle = useWatch(t19);
  let t20;
  if ($[42] !== form.control) {
    t20 = {
      control: form.control,
      name: "associatedPortfolioTitle"
    };
    $[42] = form.control;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const associatedPortfolioTitle = useWatch(t20);
  let t21;
  if ($[44] !== associatedPortfolioTitle || $[45] !== associatedUnitTitle || $[46] !== isDemand || $[47] !== tRoomListing) {
    t21 = !isDemand && associatedUnitTitle ? <Tooltip><TooltipTrigger asChild={true}><span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs text-primary-foreground"><span className="font-medium capitalize truncate max-w-[120px]">{associatedUnitTitle}</span>{associatedPortfolioTitle && <span className="opacity-80 capitalize truncate max-w-[160px]">· {associatedPortfolioTitle}</span>}</span></TooltipTrigger><TooltipContent side="bottom" className="max-w-xs">{tRoomListing("houseDetails.associatedPillTooltip")}</TooltipContent></Tooltip> : undefined;
    $[44] = associatedPortfolioTitle;
    $[45] = associatedUnitTitle;
    $[46] = isDemand;
    $[47] = tRoomListing;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const associationBadge = t21;
  let t22;
  if ($[49] !== associatedPortfolioTitle || $[50] !== associatedUnitTitle || $[51] !== isDemand) {
    t22 = !isDemand && associatedUnitTitle ? <span className="capitalize truncate"><span className="font-medium">{associatedUnitTitle}</span>{associatedPortfolioTitle && <span className="opacity-80"> · {associatedPortfolioTitle}</span>}</span> : undefined;
    $[49] = associatedPortfolioTitle;
    $[50] = associatedUnitTitle;
    $[51] = isDemand;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const associationMobileBanner = t22;
  let t23;
  if ($[53] !== defaultListingType || $[54] !== tRoomListing) {
    t23 = !defaultListingType ? [{
      id: 0,
      title: tRoomListing("steps.prescreen.title"),
      description: tRoomListing("steps.prescreen.description"),
      content: <ListingTypePrescreen />,
      fieldNames: ["listingType"],
      hideNavigation: true
    }] : [];
    $[53] = defaultListingType;
    $[54] = tRoomListing;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== tRoomListing) {
    t24 = tRoomListing("steps.roomDetails.title");
    $[56] = tRoomListing;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <RoomDetails />;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== tRoomListing) {
    t26 = tRoomListing("steps.roomDetails.description");
    $[59] = tRoomListing;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== isDemand) {
    t27 = !isDemand ? <PropertyAssociationSelect /> : undefined;
    $[61] = isDemand;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = ["title", "rentPrice", "description", "availableFrom"];
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t24 || $[65] !== t26 || $[66] !== t27) {
    t29 = {
      id: 0,
      title: t24,
      content: t25,
      description: t26,
      belowCardContent: t27,
      fieldNames: t28
    };
    $[64] = t24;
    $[65] = t26;
    $[66] = t27;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  const t30 = isDemand ? "steps.houseDetails.demandTitle" : "steps.houseDetails.title";
  let t31;
  if ($[68] !== t30 || $[69] !== tRoomListing) {
    t31 = tRoomListing(t30);
    $[68] = t30;
    $[69] = tRoomListing;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <HouseDetails />;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  const t33 = isDemand ? "steps.houseDetails.demandDescription" : "steps.houseDetails.description";
  let t34;
  if ($[72] !== t33 || $[73] !== tRoomListing) {
    t34 = tRoomListing(t33);
    $[72] = t33;
    $[73] = tRoomListing;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== isDemand) {
    t35 = isDemand ? ["locationCodes"] : ["latitude", "longitude"];
    $[75] = isDemand;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t31 || $[78] !== t34 || $[79] !== t35) {
    t36 = {
      id: 0,
      title: t31,
      content: t32,
      description: t34,
      fieldNames: t35
    };
    $[77] = t31;
    $[78] = t34;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== isDemand || $[82] !== tRoomListing) {
    t37 = !isDemand ? [{
      id: 0,
      title: tRoomListing("steps.images.title"),
      description: tRoomListing("steps.images.description"),
      content: <NewRoomListingImagesForm />,
      fieldNames: ["images"]
    }] : [];
    $[81] = isDemand;
    $[82] = tRoomListing;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== tRoomListing) {
    t38 = tRoomListing("steps.review.title");
    $[84] = tRoomListing;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== tRoomListing) {
    t39 = tRoomListing("steps.review.description");
    $[86] = tRoomListing;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  let t40;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <RevisionDetails />;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t38 || $[90] !== t39) {
    t41 = {
      id: 0,
      title: t38,
      description: t39,
      content: t40
    };
    $[89] = t38;
    $[90] = t39;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== t23 || $[93] !== t29 || $[94] !== t36 || $[95] !== t37 || $[96] !== t41) {
    t42 = [...t23, t29, t36, ...t37, t41];
    $[92] = t23;
    $[93] = t29;
    $[94] = t36;
    $[95] = t37;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== associationBadge || $[99] !== associationMobileBanner || $[100] !== t42) {
    t43 = t42.map((step, index) => ({
      ...step,
      id: index + 1,
      titleBadge: associationBadge,
      mobileBanner: associationMobileBanner
    }));
    $[98] = associationBadge;
    $[99] = associationMobileBanner;
    $[100] = t42;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  const steps = t43;
  const t44 = newRoomListingMutation.isPending || adminCreateListingMutation.isPending;
  let t45;
  if ($[102] !== form || $[103] !== handleOnSubmit || $[104] !== isEmailVerified || $[105] !== session || $[106] !== steps || $[107] !== t44 || $[108] !== tRoomListing) {
    t45 = {
      tRoomListing,
      steps,
      isSubmitting: t44,
      form,
      handleOnSubmit,
      session,
      isEmailVerified
    };
    $[102] = form;
    $[103] = handleOnSubmit;
    $[104] = isEmailVerified;
    $[105] = session;
    $[106] = steps;
    $[107] = t44;
    $[108] = tRoomListing;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  return t45;
}